/* @flow */

import type { ParentItem } from "app/Header/Drawer";
import type { Match } from "react-router";

import React, { useContext, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import cn from "classnames";
import { useTranslate } from "@awardit/react-use-translate";
import { StoreInfoContext } from "entrypoint/shared";
import { QuoteData, AdventCalendarData } from "state/data";
import { useData } from "crustate/react";
import { Wrapper, CartMini, Button } from "@crossroads/ui-components";
import Drawer from "app/Header/Drawer";
import CartCounter from "app/Header/CartCounter";
import { adventCalendarActive } from "state/advent-calendar";
import SearchMenu from "../SearchMenu";
import Logo from "components/Logo";
import HamburgerMenu from "app/Header/HamburgerMenu";
import MobileNavHeader from "../MobileNavHeader";
import { useUi, MODE } from "helpers/ui";
import SearchIcon from "icons/search.svg";
import CustomerServiceIcon from "icons/customer-service.svg";
import HamburgerIcon from "icons/hamburger.svg";
import CloseIcon from "icons/close.svg";

import styles from "./styles.scss";

const NavLinks = (): React$Node => {
  const t = useTranslate();
  const location = useLocation();
  const adventCalendar = useData(AdventCalendarData);
  const testDate = new URLSearchParams(location.search).get("testDate");
  const { info: { time } } = useContext(StoreInfoContext);
  const currentDate = testDate ? new Date(testDate) : new Date(time);
  const startDate = adventCalendar.state === "LOADED" ? adventCalendar.data.startDate : "";
  const endDate = adventCalendar.state === "LOADED" ? adventCalendar.data.endDate : "";
  const showAdventCalendar = adventCalendarActive(currentDate, time, startDate, endDate);

  const {
    viewMode,
    setViewMode,
  } = useUi();

  const navLinkIsActive = (match: Match) => {
    if (!match) {
      return false;
    }

    if (viewMode !== MODE.NORMAL) {
      return false;
    }

    return true;
  };

  const toggleCategories = () =>
    setViewMode(viewMode === MODE.CATEGORIES ? MODE.NORMAL : MODE.CATEGORIES);

  return (
    <>
      {showAdventCalendar &&
        <NavLink
          to={t("ADVENT_CALENDAR.LINK")}
          activeClassName={styles.active}
          className={cn(styles.item, styles.item__adventcalendar)}
          isActive={navLinkIsActive}
        >
          <span>{t("ADVENT_CALENDAR.TEXT")}</span>
        </NavLink>
      }
      <NavLink
        to="/all-products"
        activeClassName={styles.active}
        className={cn(
          styles.item,
          styles.item__allproducts
        )}
        isActive={navLinkIsActive}
      >
        <span>{t("ALL_PRODUCTS.TITLE")}</span>
      </NavLink>
      <Button
        type="button"
        aria-controls="desktopCategories"
        aria-expanded={viewMode === MODE.CATEGORIES ?? "false"}
        className={
          cn(
            styles.item,
            styles.item__categories,
            { [styles.active]: viewMode === MODE.CATEGORIES }
          )
        }
        onClick={toggleCategories}
      >
        <span>{t("HEADER.CATEGORIES")}</span>
      </Button>
    </>
  );
};

const DesktopNavHeader = ({
  heading,
  onClose,
}: { heading: string, onClose: () => void }): React$Node => {
  const t = useTranslate();

  return (
    <Wrapper className={styles.desktopNavHeader}>
      <h3>{heading}</h3>
      <Button aria-label={t("HEADER.CLOSE_MENU")} onClick={onClose}><CloseIcon aria-label={t("ICONS.CROSS")} /></Button>
    </Wrapper>
  );
};

const mapBrands = (brands: $ReadOnlyArray<string>): Array<ParentItem> => {
  const grouped = {};

  brands.forEach(brand => {
    const firstLetter = brand[0].toLowerCase();
    const array = (grouped[firstLetter] || (grouped[firstLetter] = []));

    array.push({
      name: brand,
      url: `/brand/${encodeURIComponent(brand)}`,
    });
  });

  return Object.keys(grouped)
    .sort((a, b) => a.localeCompare(b))
    .map(letter => ({
      name: letter.toUpperCase(),
      url: null,
      children: grouped[letter]
        .sort((a, b) => a.name.localeCompare(b.name, { caseInsensitive: true })),
    }));
};

const MobileSubNavigations = ({ onClose }: {
  onClose: () => void,
}) => {
  const t = useTranslate();
  const { categories, brands } = useContext(StoreInfoContext);

  const {
    setSubNavOpen,
    viewMode,
    setViewMode,
  } = useUi();

  const closeMenu = () => setViewMode(MODE.NORMAL);

  return (
    <>
      {viewMode === MODE.SEARCH &&
        <div role="search" aria-label={t("HEADER.SEARCH")} className={cn(styles.mobile, styles.subNav, styles.search)}>
          <SearchMenu heading={t("HEADER.SEARCH")} onClose={onClose} onItemClick={closeMenu} onBack={() => setSubNavOpen(false)} />
        </div>
      }

      {viewMode === MODE.CATEGORIES &&
        <nav id="categoriesMobile" aria-label={t("HEADER.CATEGORIES")} className={cn(styles.mobile, styles.subNav, styles.categories)}>
          <MobileNavHeader
            heading={t("HEADER.CATEGORIES")}
            onBack={() => {
              setSubNavOpen(false);
            }}
            onClose={onClose}
          />

          <Drawer items={categories} />
        </nav>
      }

      {viewMode === MODE.BRANDS &&
        <nav id="brandsMobile" aria-label={t("HEADER.BRANDS")} className={cn(styles.mobile, styles.subNav, styles.brands)}>
          <MobileNavHeader
            heading={t("HEADER.BRANDS")}
            onBack={() => {
              setSubNavOpen(false);
            }}
            onClose={onClose}
          />

          <Drawer items={mapBrands(brands)} />
        </nav>
      }
    </>
  );
};

const DesktopSubNavigations = ({ onClose }: {
  onClose: () => void,
}): React$Node => {
  const t = useTranslate();
  const { categories, brands } = useContext(StoreInfoContext);
  const ref = useRef<?HTMLElement>(null);

  const {
    setHamburgerOpen,
    setSubNavOpen,
    viewMode,
    setViewMode,
  } = useUi();

  const closeMenu = () => setViewMode(MODE.NORMAL);

  useEffect(() => {
    if (ref.current && (viewMode === MODE.CATEGORIES || viewMode === MODE.BRANDS)) {
      ref.current.querySelector("button")?.focus();
    }
  }, [viewMode]);

  useEffect(() => {
    const closeOnEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    window.addEventListener("keydown", closeOnEscape);

    return () =>
      window.removeEventListener("keydown", closeOnEscape);
  }, []);

  return (
    <>
      {viewMode === MODE.CATEGORIES &&
        <nav
          ref={ref}
          id="desktopCategories"
          aria-label={t("HEADER.CATEGORIES")}
          className={cn(
            styles.desktop,
            styles.subNav,
            styles.categories
          )}
        >
          <DesktopNavHeader heading={t("HEADER.CATEGORIES")} onClose={onClose} />
          <Drawer
            setHamburgerOpen={setHamburgerOpen}
            heading={t("HEADER.CATEGORIES")}
            items={categories}
            onClose={onClose}
            onItemClick={closeMenu}
            onBack={() => setSubNavOpen(false)}
          />
        </nav>
      }

      {viewMode === MODE.BRANDS &&
        <nav
          ref={ref}
          id="desktopBrands"
          aria-label={t("HEADER.BRANDS")}
          className={cn(
            styles.desktop,
            styles.subNav,
            styles.brands
          )}
        >
          <DesktopNavHeader heading={t("HEADER.BRANDS")} onClose={onClose} />
          <Drawer
            setHamburgerOpen={setHamburgerOpen}
            heading={t("HEADER.BRANDS")}
            items={mapBrands(brands)}
            onClose={onClose}
            onItemClick={closeMenu}
            onBack={() => setSubNavOpen(false)}
          />
        </nav>
      }
    </>
  );
};

const Nav = ({
  onCheckout,
}: { onCheckout: boolean }): React$Node => {
  const t = useTranslate();
  const location = useLocation();
  const quoteData = useData(QuoteData);
  const showHeader = true;
  const {
    setHamburgerOpen,
    toggleHamburger,
    setSubNavOpen,
    viewMode,
    setViewMode,
    closeHamburger,
    hamburgerOpen,
    isDesktop,
  } = useUi();

  const toggleSearch = () =>
    setViewMode(viewMode === MODE.SEARCH ? MODE.NORMAL : MODE.SEARCH);

  const navLinkIsActive = (match: Match) => {
    if (!match) {
      return false;
    }

    if (viewMode !== MODE.NORMAL) {
      return false;
    }

    return true;
  };

  // Close menus when switching page
  useEffect(() => {
    setViewMode(MODE.NORMAL);
    setSubNavOpen(false);
    setHamburgerOpen(false);
  }, [setViewMode, setSubNavOpen, setHamburgerOpen, location.pathname]);

  return (
    <div className={cn(styles[`mode__${viewMode}`])}>
      <nav aria-label={t("HEADER.MAIN_NAVIGATION")} className={styles.headerNav}>
        <Wrapper className={styles.wrapper}>
          <div className={styles.left}>
            <Link className={cn(styles.item, styles.item__logo)} to="/">
              <Logo
                className={styles.logo}
                onClick={() => setViewMode(MODE.NORMAL)}
              />
            </Link>
          </div>
          <div className={styles.right}>
            {!onCheckout && (
              <div className={styles.links}>
                <NavLinks />
              </div>
            )}
            <div className={styles.utilities}>
              {!onCheckout &&
                <Button
                  type="button"
                  aria-label={viewMode === MODE.SEARCH ? t("SEARCH.OPEN") : t("SEARCH.CLOSE")}
                  aria-expanded={viewMode === MODE.SEARCH ?? "true"}
                  aria-controls="searchForm"
                  className={cn(
                    styles.item,
                    styles.item__search,
                    { [styles.active]: viewMode === MODE.SEARCH }
                  )}
                  onClick={toggleSearch}
                >
                  <SearchIcon aria-label={t("ICONS.SEARCH")} />
                </Button>
              }
              {viewMode === MODE.SEARCH &&
                <div role="search" aria-label={t("HEADER.SEARCH")} className={cn(styles.desktop, styles.search)}>
                  <SearchMenu onClose={() => toggleSearch()} />
                </div>
              }
              {!onCheckout &&
                <NavLink
                  to={t("CUSTOMER_SERVICE.LINK")}
                  aria-label={t("HEADER.CONTACT")}
                  activeClassName={styles.active}
                  className={cn(styles.item, styles.item__customerService)}
                  isActive={navLinkIsActive}
                >
                  <CustomerServiceIcon aria-label={t("ICONS.HEADPHONES")} />
                </NavLink>
              }
              <CartCounter
                className={cn(styles.item, styles.item__cartCounter)}
                isOpen={viewMode === MODE.CART}
                openMiniCart={() =>
                  setViewMode(viewMode === MODE.CART ? MODE.NORMAL : MODE.CART)
                }
              />
            </div>
          </div>
          {!onCheckout &&
            <Button
              type="button"
              aria-controls="hamburgerMenu"
              aria-expanded={hamburgerOpen ?? "false"}
              aria-label={t("HEADER.OPEN_MENU")}
              className={cn(styles.item, styles.hamburger, styles.padding)}
              onClick={toggleHamburger}
            >
              <HamburgerIcon aria-label={t("ICONS.HAMBURGER")} />
            </Button>
          }

          {viewMode === MODE.CART && isDesktop &&
            <CartMini
              quoteData={quoteData}
              showHeader={showHeader}
              ctaUrl={t("URL.CHECKOUT_CART")}
              className={cn(styles.desktop, styles.miniCart)}
              onClose={() => setViewMode(MODE.NORMAL)}
            />
          }
        </Wrapper>
        {!onCheckout &&
          <HamburgerMenu>
            <MobileSubNavigations
              className={styles.mobile}
              onClose={() => closeHamburger(styles.animationDuration)}
            />
          </HamburgerMenu>
        }
      </nav>

      <DesktopSubNavigations
        onClose={() => setViewMode(MODE.NORMAL)}
      />

      {viewMode === MODE.CART && !isDesktop &&
        <CartMini
          quoteData={quoteData}
          showHeader={showHeader}
          ctaUrl={t("URL.CHECKOUT_CART")}
          className={cn(styles.mobile, styles.miniCart)}
          onClose={() => setViewMode(MODE.NORMAL)}
        />
      }

      {viewMode !== MODE.NORMAL &&
        <div className={styles.dim} onClick={() => setViewMode(MODE.NORMAL)} />
      }
    </div>
  );
};

export default Nav;
